import { useState, useMemo } from 'react'
import {
  toDateOnly,
  addDays,
  toIsoDate,
} from '../../worker-calendar/model/useWorkerCalendarDays'

/**
 * Start on a day this overlay would actually draw work on — Sunday snaps forward to Monday.
 *
 * Declared here rather than borrowed from `useWorkerCalendarDays`. That hook's anchor now steps
 * over the whole weekend, because the worker calendar stopped drawing Saturday columns; this
 * overlay still walks raw calendar days (`addDays`, three at a time) and does draw them, so taking
 * its anchor rule would have left the two halves disagreeing about what a weekend is.
 */
function getInitialAnchor(today: Date): Date {
  const d = toDateOnly(today)
  return d.getDay() === 0 ? addDays(d, 1) : d
}

export function useLocalCalendarDays() {
  const today = toDateOnly(new Date())
  const [anchor, setAnchor] = useState<Date>(() => getInitialAnchor(today))

  const days = useMemo(() => [anchor, addDays(anchor, 1), addDays(anchor, 2)], [anchor])

  const from = useMemo(
    () => new Date(days[0].getFullYear(), days[0].getMonth(), days[0].getDate(), 0, 0, 0, 0).toISOString(),
    [days],
  )
  const to = useMemo(
    () => new Date(days[2].getFullYear(), days[2].getMonth(), days[2].getDate(), 23, 59, 59, 999).toISOString(),
    [days],
  )

  const todayIso = toIsoDate(today)
  const dayIsos = days.map(toIsoDate)

  function next() {
    setAnchor(addDays(anchor, 3))
  }

  function prev() {
    setAnchor(addDays(anchor, -3))
  }

  function goToToday() {
    setAnchor(getInitialAnchor(today))
  }

  return { days, dayIsos, from, to, todayIso, prev, next, goToToday }
}
